import {
  type DirectorySubmissionFull,
  type SubmissionFormData,
} from "@/components/DirectorySubmissions/Form/types";

/**
 * Cleans a string by replacing all newline characters (\n, \r\n, \r)
 * with a space. Also handles null/undefined inputs.
 */
const cleanString = (str: string | null | undefined): string => {
  if (!str) return "";
  // This regex handles all common newline variations
  return str.replace(/(\r\n|\n|\r)/gm, " ");
};

export const submissionToFormData = (
  submission: DirectorySubmissionFull,
): SubmissionFormData => {
  const data: SubmissionFormData = {};

  // Populate overview data
  data.A1 = cleanString(submission.firm_name);
  data.A2 = cleanString(submission.practice_area_name);
  data.A3 = cleanString(submission.location);
  data.B1 = cleanString(submission.department_name);
  data.B2 = cleanString(submission.partners_count);
  data.B3 = cleanString(submission.other_lawyers_count);
  data.B7 = cleanString(submission.department_specialties);
  data.C2 = cleanString(submission.coverage_feedback);

  data.A4 = submission.contactPersons
    .sort((a, b) => a.row_order - b.row_order)
    .map((cp) => ({
      id: cp.id,
      row_order: cp.row_order,
      value: [
        cleanString(cp.name),
        cleanString(cp.email),
        cleanString(cp.telephone_number),
      ],
    }));

  data.B4 = submission.departmentHeads
    .sort((a, b) => a.row_order - b.row_order)
    .map((dh) => ({
      id: dh.id,
      row_order: dh.row_order,
      value: [
        cleanString(dh.name),
        cleanString(dh.email),
        cleanString(dh.telephone_number),
      ],
    }));

  data.B5 = submission.partnerChanges
    .sort((a, b) => a.row_order - b.row_order)
    .map((pc) => ({
      id: pc.id,
      row_order: pc.row_order,
      value: [
        cleanString(pc.name),
        cleanString(pc.joined_departed),
        cleanString(pc.joined_from_destination),
      ],
    }));

  data.B6 = submission.lawyerInfo
    .sort((a, b) => a.row_order - b.row_order)
    .map((li) => ({
      id: li.id,
      row_order: li.row_order,
      value: [
        cleanString(li.name),
        cleanString(li.comments_or_web_link),
        li.is_partner ?? null,
        li.is_ranked ?? null,
        cleanString(li.additional_info),
      ],
    }));

  data.C1 = submission.barristersUsed
    .sort((a, b) => a.row_order - b.row_order)
    .map((bu) => ({
      id: bu.id,
      row_order: bu.row_order,
      value: [
        cleanString(bu.name),
        cleanString(bu.firm_or_set),
        cleanString(bu.comments),
      ],
    }));

  data.PC = submission.publishableClients
    .sort((a, b) => a.row_order - b.row_order)
    .map((pc) => ({
      id: pc.id,
      row_order: pc.row_order,
      value: [cleanString(pc.client_name), pc.is_new_client ?? null],
    }));

  data.CC = submission.confidentialClients
    .sort((a, b) => a.row_order - b.row_order)
    .map((cc) => ({
      id: cc.id,
      row_order: cc.row_order,
      value: [cleanString(cc.client_name), cc.is_new_client ?? null],
    }));

  // Populate matter data
  const populateMatters = (
    matters: typeof submission.matters,
    prefix: string,
  ) => {
    matters.forEach((matter, index) => {
      const key = `${prefix}${index + 1}`;
      data[key] = {
        0: cleanString(matter.client_name),
        1: cleanString(matter.matter_description),
        2: cleanString(matter.value),
        3: cleanString(matter.cross_border),
        4: cleanString(matter.lead_partner_name),
        5: cleanString(matter.other_team_members),
        6: cleanString(matter.other_firms),
        7: cleanString(matter.date_of_completion),
        8: cleanString(matter.other_info),
        id: matter.id.toString(),
        included_order: matter.included_order,
      };
    });
  };

  populateMatters(
    submission.matters
      .filter((matter) => matter.is_public)
      .sort((a, b) => (a.included_order ?? 0) - (b.included_order ?? 0)),
    "PM",
  );

  populateMatters(
    submission.matters
      .filter((matter) => !matter.is_public)
      .sort((a, b) => (a.included_order ?? 0) - (b.included_order ?? 0)),
    "CM",
  );

  return data;
};
